/**
 * Opposition cases service — live from W10.
 *
 * The product says *opposition case*; the API says **procedure**. This file is
 * the translation layer and the only place in the SPA that knows both words
 * (the routes stay `/oppositions/...`).
 *
 * Live contract (`backend/src/lexentiqa/api/routers/procedures.py`):
 *
 *   GET    /procedures?status=&active=&priority=&registry=&deadline=&q=
 *                     &client_id=&brand_id=&lawyer_id=&sort=&limit=&offset=
 *   GET    /procedures/summary
 *   GET    /procedures/activity?limit=&days=
 *   GET    /procedures/draft?match_id=
 *   POST   /procedures                       { match_id | brand_id + conflict_* , … }
 *   GET    /procedures/:id
 *   PATCH  /procedures/:id
 *   POST   /procedures/:id/transition        { to, outcome?, reason?, filing_date?, client_visible? }
 *   DELETE /procedures/:id                   (firm main account only)
 *   GET    /procedures/:id/mark-logo
 *   GET    /procedures/:id/events
 *   POST   /procedures/:id/notes             { body, category, client_visible }
 *   GET    /procedures/:id/documents          POST (multipart)  PATCH / DELETE …/:doc
 *   GET    /procedures/:id/documents/:doc/content
 *   POST   /procedures/:id/deadlines          PATCH / DELETE …/:did
 *
 * The mock branch returns the same shapes and **filters its fixtures the way
 * the API filters rows** — a page developed against mocks that do not filter
 * looks right and ships with dead checkboxes (the monitoring page's
 * "filter the service forwards half of" trap, `@frontend`).
 */
import { apiResolve, http, dedupe, HttpError, ValidationError } from '@/api'
import { isLive } from '@/api/config'
import {
  ACTIVE_STATUSES,
  createDeadline,
  createOpposition,
  createOppositionDetail,
  initialsOf,
} from '@/entities/oppositionCase'
import type {
  CaseBrand,
  CaseDeadline,
  CaseMark,
  CaseMatch,
  CaseOutcome,
  CasePriority,
  CaseStatus,
  DeadlineKind,
  DocumentCategory,
  NoteCategory,
  OppositionCase,
  OppositionCaseDetail,
} from '@/entities/oppositionCase'
import type { RiskLevel } from '@/entities/raw'
import fixtureJson from '@/mock/oppositions-list.json'
import monitoringFixture from '@/mock/monitoring.json'
import type { Paged } from './clients.service'
import type { ServiceOptions } from './types'

const LIVE = () => isLive('procedures')

/* ═════════════════════════════════════════════════════════ API shapes */

export interface ApiPerson {
  id: number
  name: string
}

export interface ApiCaseBrand {
  id: number
  title: string
  application_number: string | null
  registration_date: string | null
  mark_type: string | null
  country: string | null
  classes: number[]
  has_logo: boolean
}

export interface ApiCaseMark {
  id: number | null
  title: string | null
  application_number: string | null
  owner: string | null
  mark_type: string | null
  application_date: string | null
  classes: number[]
  deadline: string | null
  has_logo: boolean
}

export interface ApiCaseMatch {
  id: number
  risk: RiskLevel
  source: 'judge' | 'rules'
  verdict: string | null
  score: number
  justification: string | null
}

export interface ApiDeadline {
  id: number
  title: string
  due_date: string
  kind: DeadlineKind
  source: 'manual' | 'mark' | 'rule'
  provisional: boolean
  note: string | null
  done_at: string | null
  days_left: number | null
}

export interface ApiProcedure {
  id: number
  title: string
  case_number: string | null
  procedure_type: string
  status: CaseStatus
  priority: CasePriority
  outcome: CaseOutcome | null
  grounds: string | null
  registry: string | null
  client: { id: number; name: string }
  brand: ApiCaseBrand
  mark: ApiCaseMark
  match: ApiCaseMatch | null
  lawyer: ApiPerson | null
  filing_date: string | null
  next_deadline: ApiDeadline | null
  documents: number
  notes: number
  last_activity_at: string | null
  created_at: string
  updated_at: string
  closed_at: string | null
}

export interface ApiProcedureDetail extends ApiProcedure {
  internal_notes: string | null
  deadlines: ApiDeadline[]
  transitions: CaseStatus[]
  created_by: ApiPerson | null
  can_edit: boolean
}

export interface ApiEvent {
  id: number
  procedure_id: number
  kind: string
  category: string | null
  body: string | null
  payload: Record<string, any> | null
  client_visible: boolean
  actor: ApiPerson | null
  actor_name: string | null
  created_at: string
}

export interface ApiActivity extends ApiEvent {
  case_title: string
  case_number: string | null
  client_id?: number | null
  client_name?: string | null
}

export interface ApiCaseDocument {
  id: number
  procedure_id: number
  category: DocumentCategory
  file_name: string
  content_type: string
  size_bytes: number
  client_visible: boolean
  uploaded_by: ApiPerson | null
  created_at: string
}

export interface ApiCount {
  key: string
  count: number
}

export interface ApiSummary {
  total: number
  active: number
  by_status: Record<string, number>
  by_priority: Record<string, number>
  by_registry: ApiCount[]
  overdue: number
  due_14: number
  due_30: number
  due_90: number
  awaiting_response: number
  opened_this_month: number
  resolved_this_year: number
  won_this_year: number
  lawyers: ApiCount[]
}

export interface ApiDraft {
  match_id: number
  title: string
  registry: string
  client: { id: number; name: string }
  brand: ApiCaseBrand
  mark: ApiCaseMark
  match: ApiCaseMatch
  existing_case_id: number | null
  /** The mark's deadline follows a rule the lawyers have not confirmed (D-037). */
  deadline_provisional?: boolean
}

export interface ApiUser {
  id: number
  user_kind: string
  display_name: string
  email?: string
  active: boolean
}

/* ═════════════════════════════════════════════════════════ UI shapes */

export type DeadlineWindow = 'overdue' | '14' | '30' | '90'
export type CaseSort = 'deadline' | 'recent' | 'priority' | 'created'
export const CASE_SORTS: readonly CaseSort[] = ['deadline', 'recent', 'priority', 'created']
export const DEADLINE_WINDOWS: readonly DeadlineWindow[] = ['overdue', '14', '30', '90']

/** What the list page can ask for. Arrays are multi-select checkboxes. */
export interface CaseFilters {
  status?: CaseStatus[]
  priority?: CasePriority[]
  registry?: string[]
  deadline?: DeadlineWindow | null
  active?: boolean | null
  q?: string
  clientId?: number | string | null
  brandId?: number | string | null
  lawyerId?: number | string | null
  sort?: CaseSort | null
  limit?: number
  offset?: number
}

/** The KPI row and every filter count — named as the UI names things. */
export interface OppositionSummary {
  total: number
  active: number
  byStatus: Record<string, number>
  byPriority: Record<string, number>
  byRegistry: ApiCount[]
  overdue: number
  due14: number
  due30: number
  due90: number
  awaitingResponse: number
  openedThisMonth: number
  resolvedThisYear: number
  /** Won *or settled* — the API counts both, and the page says so. */
  wonThisYear: number
}

export const EMPTY_SUMMARY: OppositionSummary = {
  total: 0,
  active: 0,
  byStatus: {},
  byPriority: {},
  byRegistry: [],
  overdue: 0,
  due14: 0,
  due30: 0,
  due90: 0,
  awaitingResponse: 0,
  openedThisMonth: 0,
  resolvedThisYear: 0,
  wonThisYear: 0,
}

/** One timeline line. */
export interface CaseEvent {
  id: number
  procedureId: number
  kind: string
  category: string | null
  body: string | null
  payload: Record<string, any>
  clientVisible: boolean
  /** The person as they are now, or the name snapshotted on the event. */
  actorName: string | null
  actorId: number | null
  createdAt: string
}

/** A line of the firm-wide feed, with enough of its case to link to it. */
export interface CaseActivity extends CaseEvent {
  caseTitle: string
  caseNumber: string | null
  clientId: number | null
  clientName: string | null
}

export interface CaseDocument {
  id: number
  procedureId: number
  category: DocumentCategory
  fileName: string
  contentType: string
  sizeBytes: number
  clientVisible: boolean
  uploadedBy: string | null
  createdAt: string
}

export interface CaseDraft {
  matchId: number
  title: string
  registry: string
  client: { id: number; name: string }
  brand: CaseBrand
  mark: CaseMark
  match: CaseMatch
  /** Set when the match already has an open case — send the lawyer there. */
  existingCaseId: number | null
  /** The mark's deadline follows a registry rule still under review (D-037). */
  deadlineProvisional: boolean
}

/** A member of the firm's staff a case can be assigned to. */
export interface Lawyer {
  id: number
  name: string
  kind: string
}

/** What the create form sends. Either `matchId`, or `brandId` + the conflict. */
export interface CaseCreate {
  matchId?: number | string | null
  brandId?: number | string | null
  title?: string | null
  caseNumber?: string | null
  priority?: CasePriority
  status?: 'under_review' | 'drafting'
  lawyerId?: number | null
  grounds?: string | null
  notes?: string | null
  conflictTitle?: string | null
  conflictApplicationNumber?: string | null
  conflictOwner?: string | null
  registry?: string | null
}

export interface CasePatch {
  title?: string
  caseNumber?: string | null
  priority?: CasePriority
  lawyerId?: number | null
  grounds?: string | null
  notes?: string | null
  filingDate?: string | null
}

export interface TransitionRequest {
  to: CaseStatus
  outcome?: CaseOutcome | null
  reason?: string | null
  filingDate?: string | null
  clientVisible?: boolean
}

export interface NoteRequest {
  body: string
  category: NoteCategory
  clientVisible: boolean
}

export interface DeadlineRequest {
  title: string
  dueDate: string
  kind: DeadlineKind
  note?: string | null
}

export interface DeadlinePatch {
  title?: string
  dueDate?: string
  note?: string | null
  done?: boolean
}

/* ═════════════════════════════════════════════════════════ adapters */

export function deadlineFromApi(d: ApiDeadline): CaseDeadline {
  return createDeadline({
    id: d.id,
    title: d.title,
    dueDate: d.due_date,
    kind: d.kind,
    source: d.source,
    provisional: d.provisional,
    note: d.note,
    doneAt: d.done_at,
    daysLeft: d.days_left,
  })
}

function brandFromApi(b: ApiCaseBrand) {
  return {
    id: b.id,
    title: b.title,
    applicationNumber: b.application_number,
    registrationDate: b.registration_date,
    markType: b.mark_type,
    country: b.country,
    classes: b.classes ?? [],
    hasLogo: b.has_logo,
  }
}

function markFromApi(m: ApiCaseMark) {
  return {
    id: m.id,
    title: m.title,
    applicationNumber: m.application_number,
    owner: m.owner,
    markType: m.mark_type,
    applicationDate: m.application_date,
    classes: m.classes ?? [],
    deadline: m.deadline,
    hasLogo: m.has_logo,
  }
}

function matchFromApi(m: ApiCaseMatch | null): CaseMatch | null {
  if (!m) return null
  return {
    id: m.id,
    risk: m.risk,
    source: m.source,
    verdict: m.verdict,
    score: m.score,
    justification: m.justification,
  }
}

function personFromApi(p: ApiPerson | null) {
  return p ? { id: p.id, name: p.name, initials: initialsOf(p.name) } : null
}

function rowRecord(p: ApiProcedure) {
  return {
    id: p.id,
    title: p.title,
    caseNumber: p.case_number,
    status: p.status,
    priority: p.priority,
    outcome: p.outcome,
    grounds: p.grounds,
    registry: p.registry,
    client: p.client,
    brand: brandFromApi(p.brand),
    mark: markFromApi(p.mark),
    match: matchFromApi(p.match),
    lawyer: personFromApi(p.lawyer),
    filingDate: p.filing_date,
    nextDeadline: p.next_deadline ? deadlineFromApi(p.next_deadline) : null,
    documents: p.documents,
    notes: p.notes,
    lastActivityAt: p.last_activity_at,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
    closedAt: p.closed_at,
  }
}

/** One list row. */
export function caseFromApi(p: ApiProcedure): OppositionCase {
  return createOpposition(rowRecord(p))
}

/** The dossier. */
export function caseDetailFromApi(p: ApiProcedureDetail): OppositionCaseDetail {
  return createOppositionDetail({
    ...rowRecord(p),
    internalNotes: p.internal_notes,
    deadlines: (p.deadlines ?? []).map(deadlineFromApi),
    transitions: p.transitions ?? [],
    createdBy: personFromApi(p.created_by),
    canEdit: p.can_edit,
  })
}

export function summaryFromApi(s: ApiSummary): OppositionSummary {
  return {
    total: s.total ?? 0,
    active: s.active ?? 0,
    byStatus: s.by_status ?? {},
    byPriority: s.by_priority ?? {},
    byRegistry: s.by_registry ?? [],
    overdue: s.overdue ?? 0,
    due14: s.due_14 ?? 0,
    due30: s.due_30 ?? 0,
    due90: s.due_90 ?? 0,
    awaitingResponse: s.awaiting_response ?? 0,
    openedThisMonth: s.opened_this_month ?? 0,
    resolvedThisYear: s.resolved_this_year ?? 0,
    wonThisYear: s.won_this_year ?? 0,
  }
}

export function eventFromApi(e: ApiEvent): CaseEvent {
  return {
    id: e.id,
    procedureId: e.procedure_id,
    kind: e.kind,
    category: e.category,
    body: e.body,
    payload: e.payload ?? {},
    clientVisible: e.client_visible,
    /* A viewer's users policy hides most of the firm, so `actor` is often
     * null for them; the name snapshotted on the event is still true. */
    actorName: e.actor?.name ?? e.actor_name ?? null,
    actorId: e.actor?.id ?? null,
    createdAt: e.created_at,
  }
}

export function activityFromApi(a: ApiActivity): CaseActivity {
  return {
    ...eventFromApi(a),
    caseTitle: a.case_title,
    caseNumber: a.case_number,
    clientId: a.client_id ?? null,
    clientName: a.client_name ?? null,
  }
}

export function documentFromApi(d: ApiCaseDocument): CaseDocument {
  return {
    id: d.id,
    procedureId: d.procedure_id,
    category: d.category,
    fileName: d.file_name,
    contentType: d.content_type,
    sizeBytes: d.size_bytes,
    clientVisible: d.client_visible,
    uploadedBy: d.uploaded_by?.name ?? null,
    createdAt: d.created_at,
  }
}

export function draftFromApi(d: ApiDraft): CaseDraft {
  return {
    matchId: d.match_id,
    title: d.title,
    registry: d.registry,
    client: d.client,
    brand: createOpposition({ brand: brandFromApi(d.brand) }).brand,
    mark: createOpposition({ mark: markFromApi(d.mark) }).mark,
    match: matchFromApi(d.match)!,
    existingCaseId: d.existing_case_id,
    deadlineProvisional: d.deadline_provisional ?? false,
  }
}

/**
 * Who a case may be assigned to: active staff, never a client viewer. The API
 * refuses anyone else with a 400; offering them would be a picker whose
 * choices fail on submit.
 */
export function lawyersFromApi(users: ApiUser[]): Lawyer[] {
  return users
    .filter((u) => u.active && (u.user_kind === 'firm_main' || u.user_kind === 'firm_manager'))
    .map((u) => ({ id: u.id, name: u.display_name || u.email || String(u.id), kind: u.user_kind }))
}

/** A 409 on create names the case that already exists: `existing:<id>`. */
export function existingCaseFromError(err: unknown): number | null {
  if (!(err instanceof HttpError) || err.status !== 409) return null
  const details = (err.data as any)?.error?.details
  const message: unknown = Array.isArray(details) ? details[0]?.message : null
  const found = typeof message === 'string' ? /^existing:(.+)$/.exec(message) : null
  if (!found) return null
  const n = Number(found[1])
  return Number.isFinite(n) ? n : (found[1] as unknown as number)
}

/**
 * UI filters → the query the API understands.
 *
 * Every filter the page shows is forwarded here and a test asserts each one
 * (`tests/services/oppositions.service.test.ts`). Multi-selects travel as
 * repeated parameters (`status=a&status=b`), which is what the router reads.
 */
export function toParams(filters: CaseFilters = {}): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  if (filters.status?.length) params.status = [...filters.status]
  if (filters.priority?.length) params.priority = [...filters.priority]
  if (filters.registry?.length) params.registry = [...filters.registry]
  if (filters.deadline) params.deadline = filters.deadline
  if (filters.active === true || filters.active === false) params.active = filters.active
  /* The summary counts a deadline window over *active* cases only, while the
   * list's `deadline=` filter alone would also return a resolved case whose
   * deadline was never ticked off — more rows than the number beside the
   * radio. With no status chosen, the window means "active cases due", which
   * is also the only reading that is work. A chosen status decides instead. */
  if (filters.deadline && !filters.status?.length && params.active === undefined) {
    params.active = true
  }
  const q = filters.q?.trim()
  if (q) params.q = q
  if (filters.clientId != null && filters.clientId !== '') params.client_id = filters.clientId
  if (filters.brandId != null && filters.brandId !== '') params.brand_id = filters.brandId
  if (filters.lawyerId != null && filters.lawyerId !== '') params.lawyer_id = filters.lawyerId
  if (filters.sort) params.sort = filters.sort
  if (filters.limit != null) params.limit = filters.limit
  if (filters.offset != null) params.offset = filters.offset
  return params
}

/** The create form → `ProcedureCreate`. Blank strings are not sent. */
export function createBody(payload: CaseCreate): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  const str = (v: string | null | undefined) => (v ?? '').trim() || null
  if (payload.matchId != null) body.match_id = payload.matchId
  else if (payload.brandId != null) body.brand_id = payload.brandId
  const optional: [string, string | null][] = [
    ['title', str(payload.title)],
    ['case_number', str(payload.caseNumber)],
    ['grounds', str(payload.grounds)],
    ['notes', str(payload.notes)],
    ['conflict_title', str(payload.conflictTitle)],
    ['conflict_application_number', str(payload.conflictApplicationNumber)],
    ['conflict_owner', str(payload.conflictOwner)],
    ['registry', str(payload.registry)],
  ]
  for (const [key, value] of optional) if (value) body[key] = value
  if (payload.priority) body.priority = payload.priority
  if (payload.status) body.status = payload.status
  if (payload.lawyerId != null) body.assigned_lawyer_user_id = payload.lawyerId
  return body
}

/** A case edit → `ProcedureUpdate`. Only what was given is sent. */
export function patchBody(patch: CasePatch): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (patch.title !== undefined) body.title = patch.title
  if (patch.caseNumber !== undefined) body.case_number = patch.caseNumber
  if (patch.priority !== undefined) body.priority = patch.priority
  if (patch.lawyerId !== undefined) body.assigned_lawyer_user_id = patch.lawyerId
  if (patch.grounds !== undefined) body.grounds = patch.grounds
  if (patch.notes !== undefined) body.notes = patch.notes
  if (patch.filingDate !== undefined) body.filing_date = patch.filingDate
  return body
}

export function transitionBody(t: TransitionRequest): Record<string, unknown> {
  const body: Record<string, unknown> = { to: t.to }
  if (t.outcome) body.outcome = t.outcome
  const reason = t.reason?.trim()
  if (reason) body.reason = reason
  if (t.filingDate) body.filing_date = t.filingDate
  if (t.clientVisible !== undefined) body.client_visible = t.clientVisible
  return body
}

/* ═════════════════════════════════════════════════════════ mock branch
 *
 * An in-memory copy of the fixture, built on first use. Dates are shifted so
 * the fixture's `anchor` is today — otherwise every deadline in it would be
 * overdue a month after it was written and the deadline filters would show
 * nothing. Counts and the activity feed are *derived* from the rows, exactly
 * as the API derives them, so a count beside a filter always describes the
 * rows that filter returns.
 */

type MockCase = ApiProcedureDetail & { deadlines: ApiDeadline[] }

interface MockDb {
  cases: MockCase[]
  events: Record<string, ApiEvent[]>
  documents: Record<string, ApiCaseDocument[]>
  users: ApiUser[]
  seq: number
}

const MOCK_TRANSITIONS: Record<CaseStatus, CaseStatus[]> = {
  under_review: ['drafting', 'closed'],
  drafting: ['under_review', 'filed', 'closed'],
  filed: ['awaiting_response', 'negotiation', 'resolved', 'closed'],
  awaiting_response: ['filed', 'negotiation', 'resolved', 'closed'],
  negotiation: ['awaiting_response', 'resolved', 'closed'],
  resolved: ['negotiation', 'closed'],
  closed: ['under_review'],
}

const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 }
const DATE_KEYS = new Set([
  'due_date',
  'deadline',
  'filing_date',
  'created_at',
  'updated_at',
  'closed_at',
  'done_at',
])

let mockDb: MockDb | null = null

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function dayDiff(a: string, b: string): number {
  return Math.round((Date.parse(a.slice(0, 10)) - Date.parse(b.slice(0, 10))) / 86_400_000)
}

function shiftDate(value: string, days: number): string {
  const d = new Date(value.length > 10 ? value : `${value}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return value.length > 10 ? d.toISOString() : d.toISOString().slice(0, 10)
}

function shiftAll<T>(value: T, days: number): T {
  if (Array.isArray(value)) return value.map((v) => shiftAll(v, days)) as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      out[k] =
        DATE_KEYS.has(k) && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)
          ? shiftDate(v, days)
          : shiftAll(v, days)
    }
    return out as T
  }
  return value
}

function db(): MockDb {
  if (mockDb) return mockDb
  const fixture = fixtureJson as unknown as {
    anchor: string
    cases: MockCase[]
    events: Record<string, ApiEvent[]>
    documents: Record<string, ApiCaseDocument[]>
    users: ApiUser[]
  }
  const days = dayDiff(todayIso(), fixture.anchor)
  const events = shiftAll(fixture.events, days)
  for (const [id, list] of Object.entries(events)) {
    for (const e of list) e.procedure_id = Number(id)
  }
  mockDb = {
    cases: shiftAll(fixture.cases, days),
    events,
    documents: shiftAll(fixture.documents, days),
    users: fixture.users,
    seq: 100_000,
  }
  return mockDb
}

/** Test seam: forget the in-memory mock so the next call rebuilds it. */
export function resetMockCases(): void {
  mockDb = null
}

function daysLeft(d: ApiDeadline): number | null {
  return d.done_at ? null : dayDiff(d.due_date, todayIso())
}

function nextDue(c: MockCase): ApiDeadline | null {
  const open = c.deadlines.filter((d) => !d.done_at)
  open.sort((a, b) => a.due_date.localeCompare(b.due_date) || a.id - b.id)
  return open[0] ?? null
}

function isActive(status: string): boolean {
  return (ACTIVE_STATUSES as readonly string[]).includes(status)
}

/** A stored case as the API would serve it right now. */
function mockDetail(c: MockCase): ApiProcedureDetail {
  const deadlines = [...c.deadlines]
    .sort(
      (a, b) =>
        Number(Boolean(a.done_at)) - Number(Boolean(b.done_at)) ||
        a.due_date.localeCompare(b.due_date) ||
        a.id - b.id
    )
    .map((d) => ({ ...d, days_left: daysLeft(d) }))
  const nd = nextDue(c)
  const events = db().events[String(c.id)] ?? []
  return {
    ...c,
    deadlines,
    next_deadline: nd ? { ...nd, days_left: daysLeft(nd) } : null,
    documents: (db().documents[String(c.id)] ?? []).length,
    notes: events.filter((e) => e.kind === 'note').length,
    last_activity_at: events[0]?.created_at ?? c.updated_at,
    transitions: MOCK_TRANSITIONS[c.status] ?? [],
    can_edit: true,
  }
}

function mockRow(c: MockCase): ApiProcedure {
  const {
    internal_notes: _n,
    deadlines: _d,
    transitions: _t,
    created_by: _c,
    can_edit: _e,
    ...row
  } = mockDetail(c)
  return row
}

/** `_filters()` from the router, over the fixture. */
export function filterMockCases(cases: MockCase[], params: Record<string, unknown>): MockCase[] {
  const today = todayIso()
  const list = (v: unknown) => (Array.isArray(v) ? v.map(String) : v != null ? [String(v)] : [])
  const status = list(params.status)
  const priority = list(params.priority)
  const registry = list(params.registry).map((r) => r.toUpperCase())
  return cases.filter((c) => {
    if (status.length && !status.includes(c.status)) return false
    if (params.active === true && !isActive(c.status)) return false
    if (params.active === false && isActive(c.status)) return false
    if (priority.length && !priority.includes(c.priority)) return false
    if (registry.length && !registry.includes(String(c.registry ?? '').toUpperCase())) return false
    if (params.deadline) {
      const nd = nextDue(c)
      if (!nd) return false
      const left = dayDiff(nd.due_date, today)
      if (params.deadline === 'overdue') {
        if (left >= 0) return false
      } else if (left < 0 || left > Number(params.deadline)) return false
    }
    if (params.q) {
      const q = String(params.q).toLowerCase()
      const hay = [c.title, c.case_number, c.mark.title, c.brand.title, c.client.name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!hay.includes(q)) return false
    }
    if (params.client_id != null && String(c.client.id) !== String(params.client_id)) return false
    if (params.brand_id != null && String(c.brand.id) !== String(params.brand_id)) return false
    if (params.lawyer_id != null && String(c.lawyer?.id ?? '') !== String(params.lawyer_id))
      return false
    return true
  })
}

/** `_order()` from the router. */
function sortMockCases(cases: MockCase[], sort: string): MockCase[] {
  const due = (c: MockCase) => nextDue(c)?.due_date ?? '9999-12-31'
  const out = [...cases]
  if (sort === 'recent') out.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
  else if (sort === 'created') out.sort((a, b) => b.created_at.localeCompare(a.created_at))
  else if (sort === 'priority')
    out.sort(
      (a, b) =>
        (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
        due(a).localeCompare(due(b))
    )
  else
    out.sort(
      (a, b) =>
        Number(!isActive(a.status)) - Number(!isActive(b.status)) ||
        due(a).localeCompare(due(b)) ||
        b.updated_at.localeCompare(a.updated_at)
    )
  return out
}

/** `summary()` from the router, over the fixture. */
function mockSummary(): ApiSummary {
  const cases = db().cases
  const today = todayIso()
  const by = (key: (c: MockCase) => string) =>
    cases.reduce<Record<string, number>>((acc, c) => {
      acc[key(c)] = (acc[key(c)] ?? 0) + 1
      return acc
    }, {})
  const active = cases.filter((c) => isActive(c.status))
  const dueWithin = (days: number) =>
    active.filter((c) => {
      const nd = nextDue(c)
      if (!nd) return false
      const left = dayDiff(nd.due_date, today)
      return left >= 0 && left <= days
    }).length
  const month = today.slice(0, 7)
  const year = today.slice(0, 4)
  const registries = by((c) => c.registry ?? '—')
  return {
    total: cases.length,
    active: active.length,
    by_status: by((c) => c.status),
    by_priority: by((c) => c.priority),
    by_registry: Object.entries(registries)
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count),
    overdue: active.filter((c) => {
      const nd = nextDue(c)
      return nd ? dayDiff(nd.due_date, today) < 0 : false
    }).length,
    due_14: dueWithin(14),
    due_30: dueWithin(30),
    due_90: dueWithin(90),
    awaiting_response: cases.filter((c) => c.status === 'awaiting_response').length,
    opened_this_month: cases.filter((c) => c.created_at.startsWith(month)).length,
    resolved_this_year: cases.filter(
      (c) =>
        (c.status === 'resolved' || c.status === 'closed') &&
        c.outcome &&
        (c.closed_at ?? '').startsWith(year)
    ).length,
    won_this_year: cases.filter(
      (c) =>
        (c.outcome === 'won' || c.outcome === 'settled') && (c.closed_at ?? '').startsWith(year)
    ).length,
    lawyers: [],
  }
}

function mockCase(id: string | number): MockCase {
  const c = db().cases.find((x) => String(x.id) === String(id))
  if (!c) throw new HttpError(404, 'Case not found')
  return c
}

function mockEvent(
  c: MockCase,
  kind: string,
  extra: Partial<ApiEvent> = {},
  when = new Date().toISOString()
): ApiEvent {
  const store = db()
  const event: ApiEvent = {
    id: ++store.seq,
    procedure_id: c.id,
    kind,
    category: null,
    body: null,
    payload: null,
    client_visible: false,
    actor: { id: 12, name: 'Иван Петров' },
    actor_name: 'Иван Петров',
    created_at: when,
    ...extra,
  }
  const key = String(c.id)
  store.events[key] = [event, ...(store.events[key] ?? [])]
  c.updated_at = when
  return event
}

function mockDraft(matchId: string | number): ApiDraft {
  const groups = (monitoringFixture as any).groups ?? []
  for (const g of groups) {
    for (const r of g.rows ?? []) {
      if (String(r.id) !== String(matchId)) continue
      const existing = db().cases.find(
        (c) => c.match && String(c.match.id) === String(matchId) && c.status !== 'closed'
      )
      return {
        match_id: r.id,
        title: `${g.parent.name} vs ${r.name}`,
        registry: r.jurisdiction ?? 'EUIPO',
        client: { id: 0, name: g.parent.owner ?? '' },
        brand: {
          id: g.parent.id ?? 0,
          title: g.parent.name,
          application_number: null,
          registration_date: null,
          mark_type: null,
          country: null,
          classes: [],
          has_logo: false,
        },
        mark: {
          id: null,
          title: r.name,
          application_number: null,
          owner: r.owner ?? null,
          mark_type: null,
          application_date: null,
          classes: [],
          deadline: null,
          has_logo: false,
        },
        match: {
          id: r.id,
          risk: r.risk ?? 'med',
          source: 'rules',
          verdict: null,
          score: r.score ?? 0,
          justification: null,
        },
        existing_case_id: existing?.id ?? null,
        deadline_provisional: false,
      }
    }
  }
  throw new HttpError(404, 'Match not found')
}

function mockCreate(body: Record<string, unknown>): ApiProcedureDetail {
  const store = db()
  const now = new Date().toISOString()
  let brand: ApiCaseBrand
  let client: { id: number; name: string }
  let mark: ApiCaseMark
  let match: ApiCaseMatch | null = null
  let registry: string | null = (body.registry as string | undefined)?.toUpperCase() ?? null
  if (body.match_id != null) {
    const draft = mockDraft(body.match_id as string)
    if (draft.existing_case_id != null) {
      throw new HttpError(409, 'This match already has an open case', {
        error: { details: [{ field: 'match_id', message: `existing:${draft.existing_case_id}` }] },
      })
    }
    brand = draft.brand
    client = draft.client
    mark = draft.mark
    match = draft.match
    registry = draft.registry
  } else {
    if (!body.conflict_title) {
      throw new HttpError(400, 'A case opened by hand must name the opposed mark')
    }
    const known = store.cases.find((c) => String(c.brand.id) === String(body.brand_id))
    brand = known?.brand ?? {
      id: body.brand_id as number,
      title: String(body.brand_id),
      application_number: null,
      registration_date: null,
      mark_type: null,
      country: null,
      classes: [],
      has_logo: false,
    }
    client = known?.client ?? { id: 0, name: '' }
    mark = {
      id: null,
      title: body.conflict_title as string,
      application_number: (body.conflict_application_number as string) ?? null,
      owner: (body.conflict_owner as string) ?? null,
      mark_type: null,
      application_date: null,
      classes: [],
      deadline: null,
      has_logo: false,
    }
  }
  const lawyerId = body.assigned_lawyer_user_id as number | undefined
  const lawyerUser = store.users.find((u) => u.id === lawyerId)
  const created: MockCase = {
    id: ++store.seq,
    title: (body.title as string) ?? `${brand.title} vs ${mark.title ?? 'unnamed mark'}`,
    case_number: (body.case_number as string) ?? null,
    procedure_type: 'opposition',
    status: (body.status as CaseStatus) ?? 'under_review',
    priority: (body.priority as CasePriority) ?? 'medium',
    outcome: null,
    grounds: (body.grounds as string) ?? null,
    registry,
    client,
    brand,
    mark,
    match,
    lawyer: lawyerUser ? { id: lawyerUser.id, name: lawyerUser.display_name } : null,
    filing_date: null,
    next_deadline: null,
    documents: 0,
    notes: 0,
    last_activity_at: now,
    created_at: now,
    updated_at: now,
    closed_at: null,
    internal_notes: (body.notes as string) ?? null,
    deadlines: [],
    transitions: [],
    created_by: { id: 12, name: 'Иван Петров' },
    can_edit: true,
  }
  store.cases.unshift(created)
  mockEvent(created, 'created', {
    client_visible: true,
    payload: { status: created.status, match_id: match?.id ?? null, priority: created.priority },
  })
  return mockDetail(created)
}

function mockTransition(id: string | number, body: Record<string, unknown>) {
  const c = mockCase(id)
  const to = body.to as CaseStatus
  const from = c.status
  if (!(MOCK_TRANSITIONS[from] ?? []).includes(to)) {
    throw new ValidationError(`A case cannot move from ${from} to ${to}`)
  }
  if (to === 'resolved' && !body.outcome) {
    throw new ValidationError('Say how the case ended to resolve it')
  }
  if (to === 'closed' && from !== 'resolved' && !body.reason && !body.outcome) {
    throw new ValidationError('Say why the case is being closed')
  }
  const now = new Date().toISOString()
  c.status = to
  if (to === 'filed' && !c.filing_date) c.filing_date = (body.filing_date as string) ?? todayIso()
  if (to === 'resolved' || to === 'closed') {
    if (body.outcome) c.outcome = body.outcome as CaseOutcome
    c.closed_at = c.closed_at ?? now
  }
  if (from === 'closed') {
    c.outcome = null
    c.closed_at = null
  }
  mockEvent(c, 'status_changed', {
    category: 'status',
    body: (body.reason as string) ?? null,
    client_visible: body.client_visible !== false,
    payload: { from, to, outcome: c.outcome, filing_date: c.filing_date },
  })
  return mockDetail(c)
}

/* ═════════════════════════════════════════════════════════ the service */

const opts = (signal: AbortSignal | null | undefined, path: string, method = 'GET') => ({
  signal,
  path,
  method,
})

export default {
  async list(
    filters: CaseFilters = {},
    { signal }: ServiceOptions = {}
  ): Promise<Paged<OppositionCase>> {
    const params = toParams(filters)
    if (!LIVE()) {
      return dedupe(`GET /procedures ${JSON.stringify(params)}`, async () => {
        const all = sortMockCases(
          filterMockCases(db().cases, params),
          String(params.sort ?? 'deadline')
        )
        const limit = Number(params.limit ?? 25)
        const offset = Number(params.offset ?? 0)
        const rows = await apiResolve(
          all.slice(offset, offset + limit).map(mockRow),
          opts(signal, '/procedures')
        )
        return { items: rows.map(caseFromApi), total: all.length, limit, offset }
      })
    }
    const page: Paged<ApiProcedure> = await http.get('/procedures', { params, signal })
    return { ...page, items: (page.items ?? []).map(caseFromApi) }
  },

  async summary({ signal }: ServiceOptions = {}): Promise<OppositionSummary> {
    if (!LIVE()) {
      return summaryFromApi(await apiResolve(mockSummary(), opts(signal, '/procedures/summary')))
    }
    return summaryFromApi(await http.get('/procedures/summary', { signal }))
  },

  async activity(
    {
      limit = 10,
      days,
      clientId,
    }: { limit?: number; days?: number; clientId?: string | number } = {},
    { signal }: ServiceOptions = {}
  ): Promise<CaseActivity[]> {
    const params: Record<string, unknown> = { limit }
    if (days) params.days = days
    if (clientId != null && clientId !== '') params.client_id = clientId
    if (!LIVE()) {
      const store = db()
      const since = days ? Date.now() - days * 86_400_000 : 0
      const rows: ApiActivity[] = store.cases
        .filter((c) => params.client_id == null || String(c.client.id) === String(clientId))
        .flatMap((c) =>
          (store.events[String(c.id)] ?? []).map((e) => ({
            ...e,
            case_title: c.title,
            case_number: c.case_number,
            client_id: c.client.id,
            client_name: c.client.name,
          }))
        )
        .filter((e) => Date.parse(e.created_at) >= since)
        .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id)
        .slice(0, limit)
      return (await apiResolve(rows, opts(signal, '/procedures/activity'))).map(activityFromApi)
    }
    const rows: ApiActivity[] = await http.get('/procedures/activity', { params, signal })
    return (rows ?? []).map(activityFromApi)
  },

  async get(id: string | number, { signal }: ServiceOptions = {}): Promise<OppositionCaseDetail> {
    if (!LIVE()) {
      const detail = mockDetail(mockCase(id))
      return caseDetailFromApi(await apiResolve(detail, opts(signal, `/procedures/${id}`)))
    }
    return caseDetailFromApi(await http.get(`/procedures/${id}`, { signal }))
  },

  /** What the create form is prefilled with when opened from a match (W9.M5). */
  async draft(matchId: string | number, { signal }: ServiceOptions = {}): Promise<CaseDraft> {
    if (!LIVE()) {
      return draftFromApi(await apiResolve(mockDraft(matchId), opts(signal, '/procedures/draft')))
    }
    return draftFromApi(
      await http.get('/procedures/draft', { params: { match_id: matchId }, signal })
    )
  },

  async create(
    payload: CaseCreate,
    { signal }: ServiceOptions = {}
  ): Promise<OppositionCaseDetail> {
    const body = createBody(payload)
    if (!LIVE()) {
      const created = mockCreate(body)
      return caseDetailFromApi(await apiResolve(created, opts(signal, '/procedures', 'POST')))
    }
    return caseDetailFromApi(await http.post('/procedures', body, { signal }))
  },

  async update(
    id: string | number,
    patch: CasePatch,
    { signal }: ServiceOptions = {}
  ): Promise<OppositionCaseDetail> {
    const body = patchBody(patch)
    if (!LIVE()) {
      const c = mockCase(id)
      if ('assigned_lawyer_user_id' in body) {
        const u = db().users.find((x) => x.id === body.assigned_lawyer_user_id)
        c.lawyer = u ? { id: u.id, name: u.display_name } : null
        mockEvent(c, 'assigned', { payload: { user_id: u?.id ?? null } })
      }
      if (body.priority) c.priority = body.priority as CasePriority
      if ('grounds' in body) c.grounds = (body.grounds as string) ?? null
      if ('notes' in body) c.internal_notes = (body.notes as string) ?? null
      if ('case_number' in body) c.case_number = (body.case_number as string) ?? null
      if (body.title) c.title = body.title as string
      const others = Object.keys(body).filter((k) => k !== 'assigned_lawyer_user_id')
      if (others.length) mockEvent(c, 'updated', { payload: { fields: others } })
      return caseDetailFromApi(await apiResolve(mockDetail(c), opts(signal, `/procedures/${id}`)))
    }
    return caseDetailFromApi(await http.patch(`/procedures/${id}`, body, { signal }))
  },

  /**
   * Move a case along its lifecycle. The API refuses a move that is not in
   * `transitions`, a resolution without an outcome and an abandonment without
   * a reason — the page asks for both before it sends, and shows the API's own
   * sentence if it still says no.
   */
  async transition(
    id: string | number,
    request: TransitionRequest,
    { signal }: ServiceOptions = {}
  ): Promise<OppositionCaseDetail> {
    const body = transitionBody(request)
    if (!LIVE()) {
      const moved = mockTransition(id, body)
      return caseDetailFromApi(
        await apiResolve(moved, opts(signal, `/procedures/${id}/transition`, 'POST'))
      )
    }
    return caseDetailFromApi(await http.post(`/procedures/${id}/transition`, body, { signal }))
  },

  /** Delete outright — the firm's main account only; everyone else closes. */
  async remove(id: string | number, { signal }: ServiceOptions = {}): Promise<void> {
    if (!LIVE()) {
      const store = db()
      store.cases = store.cases.filter((c) => String(c.id) !== String(id))
      await apiResolve(null, opts(signal, `/procedures/${id}`, 'DELETE'))
      return
    }
    await http.delete(`/procedures/${id}`, { signal })
  },

  /**
   * The opposed mark's picture, as an object URL. Served *through the case*,
   * because a client viewer cannot see the match. Fetched, never linked: an
   * `<img src>` at the API sends no token and draws as broken.
   */
  async markLogoUrl(id: string | number): Promise<string | null> {
    if (!LIVE()) return null
    try {
      const blob: Blob = await http.get(`/procedures/${id}/mark-logo`, { responseType: 'blob' })
      return URL.createObjectURL(blob)
    } catch {
      return null
    }
  },

  /* ---------------------------------------------------------------- timeline */

  async events(id: string | number, { signal }: ServiceOptions = {}): Promise<CaseEvent[]> {
    if (!LIVE()) {
      mockCase(id)
      const rows = db().events[String(id)] ?? []
      return (await apiResolve(rows, opts(signal, `/procedures/${id}/events`))).map(eventFromApi)
    }
    const rows: ApiEvent[] = await http.get(`/procedures/${id}/events`, { signal })
    return (rows ?? []).map(eventFromApi)
  },

  async addNote(
    id: string | number,
    note: NoteRequest,
    { signal }: ServiceOptions = {}
  ): Promise<CaseEvent> {
    const body = {
      body: note.body.trim(),
      category: note.category,
      client_visible: note.clientVisible,
    }
    if (!LIVE()) {
      if (!body.body) throw new ValidationError('A note needs some text')
      const event = mockEvent(mockCase(id), 'note', {
        body: body.body,
        category: body.category,
        client_visible: body.client_visible,
      })
      return eventFromApi(await apiResolve(event, opts(signal, `/procedures/${id}/notes`, 'POST')))
    }
    return eventFromApi(await http.post(`/procedures/${id}/notes`, body, { signal }))
  },

  /* ---------------------------------------------------------------- documents */

  async documents(id: string | number, { signal }: ServiceOptions = {}): Promise<CaseDocument[]> {
    if (!LIVE()) {
      const rows = db().documents[String(id)] ?? []
      return (await apiResolve(rows, opts(signal, `/procedures/${id}/documents`))).map(
        documentFromApi
      )
    }
    const rows: ApiCaseDocument[] = await http.get(`/procedures/${id}/documents`, { signal })
    return (rows ?? []).map(documentFromApi)
  },

  async uploadDocument(
    id: string | number,
    file: File,
    category: DocumentCategory,
    clientVisible: boolean,
    { signal }: ServiceOptions = {}
  ): Promise<CaseDocument> {
    if (!LIVE()) {
      const store = db()
      const c = mockCase(id)
      const doc: ApiCaseDocument = {
        id: ++store.seq,
        procedure_id: c.id,
        category,
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        client_visible: clientVisible,
        uploaded_by: { id: 12, name: 'Иван Петров' },
        created_at: new Date().toISOString(),
      }
      store.documents[String(id)] = [doc, ...(store.documents[String(id)] ?? [])]
      mockEvent(c, 'document_added', {
        body: doc.file_name,
        client_visible: clientVisible,
        payload: { document_id: doc.id, category, size_bytes: doc.size_bytes },
      })
      return documentFromApi(doc)
    }
    const form = new FormData()
    form.append('file', file)
    form.append('category', category)
    form.append('client_visible', String(clientVisible))
    /* No Content-Type header: the browser must set it with the boundary. */
    return documentFromApi(
      await http.request('POST', `/procedures/${id}/documents`, { body: form, signal })
    )
  },

  async updateDocument(
    id: string | number,
    documentId: number,
    patch: { category?: DocumentCategory; clientVisible?: boolean },
    { signal }: ServiceOptions = {}
  ): Promise<CaseDocument> {
    const body: Record<string, unknown> = {}
    if (patch.category) body.category = patch.category
    if (patch.clientVisible !== undefined) body.client_visible = patch.clientVisible
    if (!LIVE()) {
      const doc = (db().documents[String(id)] ?? []).find((d) => d.id === documentId)
      if (!doc) throw new HttpError(404, 'Document not found')
      Object.assign(doc, body)
      mockEvent(mockCase(id), 'updated', {
        body: doc.file_name,
        payload: { document_id: doc.id, ...body },
      })
      return documentFromApi(doc)
    }
    return documentFromApi(
      await http.patch(`/procedures/${id}/documents/${documentId}`, body, { signal })
    )
  },

  async deleteDocument(
    id: string | number,
    documentId: number,
    { signal }: ServiceOptions = {}
  ): Promise<void> {
    if (!LIVE()) {
      const store = db()
      const docs = store.documents[String(id)] ?? []
      const doc = docs.find((d) => d.id === documentId)
      store.documents[String(id)] = docs.filter((d) => d.id !== documentId)
      mockEvent(mockCase(id), 'document_removed', {
        body: doc?.file_name ?? null,
        payload: { document_id: documentId },
      })
      return
    }
    await http.delete(`/procedures/${id}/documents/${documentId}`, { signal })
  },

  /** A document's bytes as an object URL (`responseType: 'blob'`, or a PDF is corrupted). */
  async documentUrl(id: string | number, documentId: number): Promise<string | null> {
    if (!LIVE()) return null
    const blob: Blob = await http.get(`/procedures/${id}/documents/${documentId}/content`, {
      responseType: 'blob',
    })
    return URL.createObjectURL(blob)
  },

  /* ---------------------------------------------------------------- deadlines */

  async addDeadline(
    id: string | number,
    d: DeadlineRequest,
    { signal }: ServiceOptions = {}
  ): Promise<CaseDeadline> {
    const body: Record<string, unknown> = {
      title: d.title.trim(),
      due_date: d.dueDate,
      kind: d.kind,
    }
    if (d.note?.trim()) body.note = d.note.trim()
    if (!LIVE()) {
      const store = db()
      const c = mockCase(id)
      const row: ApiDeadline = {
        id: ++store.seq,
        title: body.title as string,
        due_date: d.dueDate,
        kind: d.kind,
        source: 'manual',
        provisional: false,
        note: (body.note as string) ?? null,
        done_at: null,
        days_left: null,
      }
      c.deadlines.push(row)
      mockEvent(c, 'deadline_added', {
        body: row.title,
        client_visible: d.kind === 'statutory',
        payload: { deadline_id: row.id, due_date: row.due_date, source: 'manual' },
      })
      return deadlineFromApi({ ...row, days_left: daysLeft(row) })
    }
    return deadlineFromApi(await http.post(`/procedures/${id}/deadlines`, body, { signal }))
  },

  async updateDeadline(
    id: string | number,
    deadlineId: number,
    patch: DeadlinePatch,
    { signal }: ServiceOptions = {}
  ): Promise<CaseDeadline> {
    const body: Record<string, unknown> = {}
    if (patch.title !== undefined) body.title = patch.title
    if (patch.dueDate !== undefined) body.due_date = patch.dueDate
    if (patch.note !== undefined) body.note = patch.note
    if (patch.done !== undefined) body.done = patch.done
    if (!LIVE()) {
      const c = mockCase(id)
      const row = c.deadlines.find((x) => x.id === deadlineId)
      if (!row) throw new HttpError(404, 'Deadline not found')
      if (patch.done === true && !row.done_at) {
        row.done_at = new Date().toISOString()
        mockEvent(c, 'deadline_done', {
          body: row.title,
          payload: { deadline_id: row.id, due_date: row.due_date },
        })
      } else if (patch.done === false && row.done_at) {
        row.done_at = null
        mockEvent(c, 'updated', {
          body: row.title,
          payload: { deadline_id: row.id, reopened: true },
        })
      }
      if (patch.dueDate) {
        row.due_date = patch.dueDate
        if (row.source === 'mark') {
          row.source = 'manual'
          row.provisional = false
        }
      }
      if (patch.title) row.title = patch.title
      return deadlineFromApi({ ...row, days_left: daysLeft(row) })
    }
    return deadlineFromApi(
      await http.patch(`/procedures/${id}/deadlines/${deadlineId}`, body, { signal })
    )
  },

  async deleteDeadline(
    id: string | number,
    deadlineId: number,
    { signal }: ServiceOptions = {}
  ): Promise<void> {
    if (!LIVE()) {
      const c = mockCase(id)
      const row = c.deadlines.find((x) => x.id === deadlineId)
      c.deadlines = c.deadlines.filter((x) => x.id !== deadlineId)
      mockEvent(c, 'deadline_removed', {
        body: row?.title ?? null,
        payload: { deadline_id: deadlineId, due_date: row?.due_date ?? null },
      })
      return
    }
    await http.delete(`/procedures/${id}/deadlines/${deadlineId}`, { signal })
  },

  /* ---------------------------------------------------------------- people */

  /**
   * The lawyer picker: active firm staff from `GET /users`.
   *
   * Read here rather than through `users.service.ts`, which is still on
   * fixtures — wiring the whole users domain for one picker would put a
   * half-live roster in front of the settings pages. Paged to the end rather
   * than asking past the API's 200-row cap, which would 422.
   */
  async lawyers({ signal }: ServiceOptions = {}): Promise<Lawyer[]> {
    if (!LIVE()) {
      return lawyersFromApi(await apiResolve(db().users, opts(signal, '/users')))
    }
    const all: ApiUser[] = []
    for (let offset = 0; offset < 2000; offset += 200) {
      const page: Paged<ApiUser> = await http.get('/users', {
        params: { limit: 200, offset },
        signal,
      })
      all.push(...(page.items ?? []))
      if (all.length >= (page.total ?? 0) || !(page.items ?? []).length) break
    }
    return lawyersFromApi(all)
  },
}
